import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import {
    heroiconsChevronLeft,
    heroiconsChevronRight,
    heroiconsXCircle,
} from '@ld.consumer-portal/data-uri';
import { SvgIconComponent } from '@ld.consumer-portal/ui-shared';

export interface BottomSheetItem {
  id: string;
  label: string;
  icon?: string;
  danger?: boolean;
  disabled?: boolean;
  hasChildren?: boolean;
  children?: BottomSheetItem[];
  /** Renders a non-interactive section header row instead of a button. */
  header?: boolean;
  /** Renders a non-interactive divider line instead of a button. */
  divider?: boolean;
}

@Component({
  selector: 'ld-native-bottom-sheet',
  imports: [SvgIconComponent],
  templateUrl: './native-bottom-sheet.component.html',
  styleUrl: './native-bottom-sheet.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NativeBottomSheetComponent {
  title = input.required<string>();
  items = input.required<BottomSheetItem[]>();
  closed = output<void>();
  itemClick = output<string>();

  protected readonly chevronLeftIcon = heroiconsChevronLeft;
  protected readonly chevronRightIcon = heroiconsChevronRight;
  protected readonly closeIcon = heroiconsXCircle;

  protected readonly currentItems = signal<BottomSheetItem[]>([]);
  protected readonly currentTitle = signal<string>('');
  protected readonly parentItem = signal<BottomSheetItem | null>(null);

  protected get displayItems(): BottomSheetItem[] {
    const current = this.currentItems();
    return current.length > 0 ? current : this.items();
  }

  protected get displayTitle(): string {
    const current = this.currentTitle();
    return current || this.title();
  }

  protected get showBackButton(): boolean {
    return this.parentItem() !== null;
  }

  protected onBackdropClick(): void {
    this.closed.emit();
    this.resetNavigation();
  }

  protected onCloseClick(): void {
    this.closed.emit();
    this.resetNavigation();
  }

  protected onBackClick(): void {
    this.currentItems.set([]);
    this.currentTitle.set('');
    this.parentItem.set(null);
  }

  protected onItemClick(item: BottomSheetItem): void {
    if (item.disabled) {
      return;
    }
    if (item.hasChildren && item.children?.length) {
      this.currentItems.set(item.children);
      this.currentTitle.set(item.label);
      this.parentItem.set(item);
    } else {
      this.itemClick.emit(item.id);
      this.closed.emit();
      this.resetNavigation();
    }
  }

  private resetNavigation(): void {
    this.currentItems.set([]);
    this.currentTitle.set('');
    this.parentItem.set(null);
  }
}
